import React from 'react';
import {
  FiPlus,
  FiCheckSquare,
  FiCheck,
  FiClock,
  FiAlertTriangle,
  FiRotateCw,
  FiEdit2,
  FiTrash2,
  FiLoader,
} from 'react-icons/fi';
import { toast } from '../../../utils/toast';

interface ProjectTasksTabProps {
  projectTasks: any[];
  filteredTasksList: any[];
  taskFilter: 'All' | 'To Do' | 'In Progress' | 'Complete';
  setTaskFilter: (filter: 'All' | 'To Do' | 'In Progress' | 'Complete') => void;
  isGuestUser: boolean;
  setIsTaskModalOpen: (val: boolean) => void;
  setDetailModalTask: (task: any) => void;
  toggleTaskStatus: (taskId: string) => void;
  canUserUpdateTask: (task: any) => boolean;
  setStatusModalTask: (task: any) => void;
  setSelectedStatus: (status: any) => void;
  openEditTaskModal: (task: any) => void;
  promptDeleteTask: (task: any) => void;
  deletingTaskId: string | null;
}

export const ProjectTasksTab: React.FC<ProjectTasksTabProps> = ({
  projectTasks,
  filteredTasksList,
  taskFilter,
  setTaskFilter,
  isGuestUser,
  setIsTaskModalOpen,
  setDetailModalTask,
  toggleTaskStatus,
  canUserUpdateTask,
  setStatusModalTask,
  setSelectedStatus,
  openEditTaskModal,
  promptDeleteTask,
  deletingTaskId,
}) => {
  return (
    <div className="space-y-5">
      {/* Task filters & Action bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Filter Pills */}
        <div className="p-1 bg-[#0f0f0f] border border-white/5 rounded-full inline-flex items-center gap-1">
          {(['All', 'To Do', 'In Progress', 'Complete'] as const).map((filter) => {
            const count =
              filter === 'All'
                ? projectTasks.length
                : projectTasks.filter((t) => t.status === filter).length;
            const isActive = taskFilter === filter;
            return (
              <button
                key={filter}
                onClick={() => setTaskFilter(filter)}
                className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-full cursor-pointer transition-all ${
                  isActive
                    ? 'bg-zinc-800/90 border border-white/10 text-white shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <span>{filter}</span>
                <span
                  className={`text-[11px] ${
                    isActive ? 'text-zinc-400 font-medium' : 'text-zinc-600'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Add Task Button */}
        {!isGuestUser && (
          <button
            onClick={() => setIsTaskModalOpen(true)}
            className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-full border border-white/10 bg-zinc-900/80 hover:bg-zinc-800 text-white text-xs font-medium transition-all cursor-pointer"
          >
            <FiPlus className="w-4 h-4 stroke-[2]" />
            <span>Add Task</span>
          </button>
        )}
      </div>

      {/* Tasks List */}
      <div className="space-y-3">
        {filteredTasksList.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 border border-white/5 bg-[#0f0f0f]/30 rounded-3xl text-center">
            <FiCheckSquare className="w-12 h-12 text-zinc-700 mb-3" />
            <p className="text-zinc-400 text-sm font-semibold">No tasks in this category</p>
          </div>
        ) : (
          filteredTasksList.map((task) => {
            const isCompleted = task.status === 'Complete';
            const isOverdue = task.alerts?.includes('Overdue');

            return (
              <div
                key={task.id}
                onClick={() => setDetailModalTask(task)}
                className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 p-4 sm:p-5 rounded-[22px] border transition-all group cursor-pointer ${
                  isOverdue
                    ? 'bg-rose-950/10 border-rose-900/30 hover:border-rose-800/50'
                    : task.status === 'Complete'
                      ? 'bg-[#0F0F0F] border-emerald-500/10 hover:border-emerald-500/25'
                      : 'bg-[#0F0F0F] border-white/5 hover:border-white/10'
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Checkbox / Status Indicator */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isGuestUser) return;
                      toggleTaskStatus(task.id);
                    }}
                    className={`${isGuestUser ? 'cursor-default' : 'cursor-pointer active:scale-95'} shrink-0 transition-transform`}
                  >
                    {task.status === 'Complete' ? (
                      <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 flex items-center justify-center">
                        <FiCheck className="w-2.5 h-2.5 stroke-[3]" />
                      </div>
                    ) : task.status === 'In Progress' ? (
                      <FiClock className="w-4 h-4 text-blue-400 shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border border-zinc-600 hover:border-zinc-400 transition-colors" />
                    )}
                  </button>

                  {/* Task details */}
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p
                        className={`text-xs sm:text-sm font-bold ${
                          isCompleted ? 'text-zinc-500 line-through' : 'text-white'
                        }`}
                      >
                        {task.title}
                      </p>
                      <span
                        className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
                          task.priority === 'High'
                            ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                            : task.priority === 'Medium'
                              ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                              : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                        }`}
                      >
                        {task.priority}
                      </span>

                      {isOverdue && (
                        <span className="inline-flex items-center gap-1 bg-rose-500/10 border border-rose-500/20 text-rose-400 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase">
                          <FiAlertTriangle className="w-2.5 h-2.5" />
                          Overdue
                        </span>
                      )}
                      {!isOverdue && task.alerts?.includes('Due Soon') && (
                        <span className="inline-flex items-center gap-1 bg-amber-500/10 border border-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase">
                          <FiClock className="w-2.5 h-2.5" />
                          Due Soon
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-2 text-zinc-500 text-[11px] font-medium flex-wrap">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`w-5 h-5 rounded-full ${task.assignee.color} text-[8px] font-extrabold text-white flex items-center justify-center`}
                        >
                          {task.assignee.initials}
                        </span>
                        <span>{task.assignee.name}</span>
                      </div>
                      <span className="text-zinc-600">•</span>
                      <span className={isOverdue ? 'text-rose-400 font-semibold' : ''}>
                        {task.dueDate &&
                        task.dueDate !== 'No due date' &&
                        task.dueDate !== 'No Date'
                          ? `Due ${task.dueDate}`
                          : 'No due date'}
                      </span>
                      <span className="text-zinc-600">•</span>
                      <span
                        className={`inline-flex items-center gap-1 font-semibold ${
                          task.status === 'Complete'
                            ? 'text-emerald-400'
                            : task.status === 'In Progress'
                              ? 'text-blue-400'
                              : 'text-zinc-500'
                        }`}
                      >
                        {task.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                {!isGuestUser && (
                  <div className="flex items-center gap-2 sm:gap-2.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0 self-end sm:self-auto">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!canUserUpdateTask(task)) {
                          toast.error('You do not have permission to update this task.');
                          return;
                        }
                        setStatusModalTask(task);
                        setSelectedStatus(task.status);
                      }}
                      className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-300 text-xs font-medium transition-all cursor-pointer"
                    >
                      <FiRotateCw className="w-3.5 h-3.5 text-zinc-400" />
                      <span>Status</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditTaskModal(task);
                      }}
                      className="p-2 rounded-full border border-white/10 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
                    >
                      <FiEdit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      disabled={deletingTaskId === task.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        promptDeleteTask(task);
                      }}
                      className="p-2 rounded-full border border-rose-500/20 bg-rose-500/5 hover:bg-rose-500/20 disabled:opacity-50 text-rose-500 transition-all cursor-pointer"
                      title="Delete task"
                    >
                      {deletingTaskId === task.id ? (
                        <FiLoader className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <FiTrash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
